import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Flex,
  Spin,
  Typography,
  notification,
  theme,
} from "antd";
import { CloudSyncOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/es";
import relativeTime from "dayjs/plugin/relativeTime";
import { axiosInstance } from "@/utilities";
import { API_URL_LINKED_INTEGRATION } from "@/services/XCONS_ENV";
import {
  ACTIVE_STATUSES,
  HistorialDeImportaciones,
  filasDe,
} from "./historialDeImportaciones";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

/**
 * "hace 6 minutos" en minúscula, para ir en medio de una frase; con más de un día, la fecha.
 * El `formatLastUpdate` del admin lo devuelve con mayúscula y como elemento.
 */
const cuando = (fecha: string) =>
  dayjs().diff(fecha, "hour") < 24
    ? dayjs(fecha).locale("es").fromNow()
    : dayjs(fecha).format("DD/MM/YY HH:mm");

/** Adónde lleva el placeholder: la integración la arma xcons, no se configura desde el admin. */
export const URL_PRICING = "https://www.xcons.com/pricing/";

interface EstadoDeIntegracion {
  hasIntegration: boolean;
  status?: string;
  startedAt?: string | null;
}

interface IntegrarErpProps {
  sellerCode: string;
  /** Cuando una sincronización termina, la tabla de productos tiene datos nuevos. */
  onSincronizado?: () => void;
  /** Las importaciones del seller: acá se muestran las que vinieron de la integración. */
  importaciones?: any;
  tablePropsImportaciones?: any;
  refetchImportaciones?: () => any;
  /** El botón Asociar de la última sincronización con coincidencias (xcons-docs#630). */
  onAsociarCoincidencias?: (importId: number) => void;
}

/**
 * El panel de Integrar (xcons-docs#609). Antes era un botón "Forzar integración" adentro del
 * panel de Importar, que sólo aparecía si el seller ya tenía integración — y si no la tenía,
 * nada le contaba que existía. Ahora es la forma principal de cargar productos, así que el
 * seller sin integración ve a qué se suma.
 */
export const IntegrarErp: React.FC<IntegrarErpProps> = ({
  sellerCode,
  onSincronizado,
  importaciones,
  tablePropsImportaciones,
  refetchImportaciones,
  onAsociarCoincidencias,
}) => {
  const { token } = theme.useToken();
  const [estado, setEstado] = useState<EstadoDeIntegracion | null>(null);
  const [disparando, setDisparando] = useState(false);

  const pedirEstado = async () => {
    try {
      const { data } = await axiosInstance.get(
        `${API_URL_LINKED_INTEGRATION}/${sellerCode}/status`,
      );
      setEstado(data);
    } catch {
      // Sin integración o error: se ofrece integrar.
      setEstado({ hasIntegration: false });
    }
  };

  useEffect(() => {
    pedirEstado();
  }, [sellerCode]);

  useEffect(() => {
    if (estado?.status !== "running") return;
    const interval = setInterval(pedirEstado, 3000);
    return () => clearInterval(interval);
  }, [estado?.status]);

  const estadoAnterior = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (estadoAnterior.current === "running" && estado?.status === "idle") {
      onSincronizado?.();
      refetchImportaciones?.();
    }
    estadoAnterior.current = estado?.status;
  }, [estado?.status]);

  const sincronizar = async () => {
    setDisparando(true);
    setEstado((prev) =>
      prev
        ? { ...prev, status: "running", startedAt: new Date().toISOString() }
        : prev,
    );
    try {
      await axiosInstance.post(
        `${API_URL_LINKED_INTEGRATION}/${sellerCode}/trigger`,
      );
    } catch (error: any) {
      if (error?.response?.status === 409) {
        notification.warning({ message: "La integración ya está en curso" });
      } else {
        notification.error({ message: "No se pudo disparar la integración" });
        setEstado((prev) => (prev ? { ...prev, status: "idle" } : prev));
      }
    } finally {
      setDisparando(false);
    }
  };

  if (!estado) {
    return (
      <Flex justify="center" style={{ padding: 48 }}>
        <Spin />
      </Flex>
    );
  }

  if (!estado.hasIntegration) {
    return (
      <Flex vertical style={{ padding: 24 }}>
        <Card
          style={{
            borderStyle: "dashed",
            backgroundColor: token.colorFillAlter,
          }}
        >
          <Flex
            vertical
            align="center"
            gap={16}
            style={{ padding: "32px 24px", textAlign: "center" }}
          >
            <CloudSyncOutlined
              style={{ fontSize: 48, color: token.colorPrimary }}
            />
            <Title level={4} style={{ margin: 0 }}>
              Conectá tu ERP y olvidate de cargar a mano
            </Title>
            <Text style={{ color: token.colorTextSecondary, maxWidth: 420 }}>
              Integramos tu ERP con xcons para que los precios y el stock de tus
              productos se actualicen solos, siempre al día.
            </Text>
            <Button
              type="primary"
              size="large"
              href={URL_PRICING}
              target="_blank"
              rel="noopener noreferrer"
            >
              Integrar mi ERP
            </Button>
          </Flex>
        </Card>
      </Flex>
    );
  }

  const corriendo = estado.status === "running";
  // La última que terminó, del historial de abajo: el estado de la integración no la trae.
  const ultima = filasDe(importaciones, "integracion").find(
    (r) => !ACTIVE_STATUSES.includes(r.status),
  );

  return (
    <Flex vertical style={{ padding: 24 }}>
      {/* Qué está pasando con el ERP y cuándo se actualizó por última vez (xcons-docs#609):
          antes era sólo un botón "Forzar integración" con una línea de texto. */}
      <Card
        style={{ borderStyle: "dashed", backgroundColor: token.colorFillAlter }}
      >
        <Flex justify="space-between" align="center" gap={16} wrap>
          <Flex align="center" gap={14}>
            <Flex
              align="center"
              justify="center"
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: token.colorPrimaryBg,
                flexShrink: 0,
              }}
            >
              <CloudSyncOutlined
                style={{ fontSize: 22, color: token.colorPrimary }}
              />
            </Flex>
            <Flex vertical gap={2}>
              <Flex align="center" gap={10}>
                <Text strong style={{ fontSize: 15 }}>
                  ERP integrado
                </Text>
                {corriendo ? (
                  <Badge status="processing" text="Sincronizando…" />
                ) : (
                  <Badge status="success" text="Conectado" />
                )}
              </Flex>
              <Text style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                {corriendo && estado.startedAt ? (
                  <>Empezó {cuando(estado.startedAt)}</>
                ) : ultima ? (
                  <>
                    Última sincronización: {cuando(ultima.createdAt)}
                    {ultima.status === "completed" &&
                      ` · ${ultima.updated ?? 0} productos actualizados`}
                    {ultima.status === "error" && " · terminó con error"}
                  </>
                ) : (
                  "Los precios y el stock se actualizan solos."
                )}
              </Text>
            </Flex>
          </Flex>
          <Button
            icon={<SyncOutlined spin={corriendo} />}
            loading={disparando}
            disabled={corriendo}
            onClick={sincronizar}
          >
            {corriendo ? "Sincronizando…" : "Sincronizar ahora"}
          </Button>
        </Flex>
      </Card>
      <HistorialDeImportaciones
        origen="integracion"
        datos={importaciones}
        tableProps={tablePropsImportaciones}
        refetch={refetchImportaciones}
        onAsociarCoincidencias={onAsociarCoincidencias}
      />
    </Flex>
  );
};
